import { createModule } from 'saga-slice';
import { call, put } from 'redux-saga/effects';
import inventoryService from '../services/inventoryService';
import moment from 'moment';

const inventorySlice = createModule({
    name: 'inventory',
    initialState: {
        inventories: [], // List of all inventories
        inventory: null, // Holds the entire InventoryDTO
        highlightedItem: null, // ID of the highlighted item
        loading: false, // Loading state
        error: null, // Error state
    },
    reducers: {

        // Reducer to set all inventories
        setInventories: (state, payload) => {
            state.inventories = payload;
            state.loading = false;
            state.error = null;
        },

        // Reducer to set the entire inventory
        setInventory: (state, payload) => {

            const existingInventoryIndex = state.inventories.findIndex(inv => inv.id === payload.id);
            if (existingInventoryIndex === -1) {
                state.inventories.unshift(payload); // Add new inventory to the top of the list
            } else {
                state.inventories[existingInventoryIndex] = payload; // Update existing inventory
            }

            state.inventory = payload;
            state.loading = false;
            state.error = null;
        },

        refreshInventoryItems: (state) => {
            state.inventory.inventoryItems = state.inventory.inventoryItems.map(item => ({
                ...item,
                stockValeurUnitaire: item.product.buyingPrice,
                stockTheorique: item.product.stock,
                stockReel: item.stockReel === 0 ? item.product.stock : item.stockReel,
                stockEcartQuantite: item.stockReel - item.stockTheorique
            }));
            state.inventory.totalItems = state.inventory.inventoryItems.length;
            state.inventory.totalStockValue = state.inventory.inventoryItems.reduce(
                (total, item) => total + item.stockValeurUnitaire * item.stockReel,
                0
            );
        },

        // Reducer to add or update an item in the inventory
        addOrUpdateItem: (state, payload) => {
            if (!state.inventory) return;

            const existingItemIndex = state.inventory.inventoryItems.findIndex(
                (item) => item.id === payload.id
            );

            state.inventory.id = payload.inventoryId;
            state.inventory.totalItems = state.inventory.inventoryItems.length;
            state.inventory.totalStockValue = state.inventory.inventoryItems.reduce(
                (total, item) => total + item.stockValeurUnitaire * item.stockReel,
                0
            );

            if (existingItemIndex >= 0) {
                // Remove the existing item
                state.inventory.inventoryItems.splice(existingItemIndex, 1);
                // Add the updated item to the top of the list
                state.inventory.inventoryItems.unshift(payload);

            } else {
                // Add new item to the top of the list
                state.inventory.inventoryItems.unshift(payload);
            }
            state.highlightedItem = payload.id; // Highlight the updated/added item
            state.loading = false;
            state.error = null;
        },

        // Reducer to update the quantity of an item
        updateItemQuantity: (state, payload) => {
            if (!state.inventory) return;

            const item = state.inventory.inventoryItems.find(
                (item) => item.productId === payload.productId
            );

            if (item) {
                // Update stockReel or stockTheorique
                if (payload.stockReel !== undefined) {
                    item.stockReel = payload.stockReel;
                }
                if (payload.stockTheorique !== undefined) {
                    item.stockTheorique = payload.stockTheorique;
                }

                item.stockValeurUnitaire = payload.stockValeurUnitaire

                // Automatically calculate stockEcartQuantite
                item.stockEcartQuantite = item.stockReel - item.stockTheorique;

                // Update inventory metadata
                state.inventory.totalStockValue = state.inventory.inventoryItems.reduce(
                    (total, item) => total + item.stockValeurUnitaire * item.stockReel,
                    0
                );

                state.inventory.totalItems = state.inventory.inventoryItems.length;
                state.inventory.totalStockValue = state.inventory.inventoryItems.reduce(
                    (total, item) => total + item.stockValeurUnitaire * item.stockReel,
                    0
                );

                // Update the inventory items with the changed item
                state.inventory.inventoryItems = state.inventory.inventoryItems.map(
                    (invItem) => (invItem.productId === item.productId ? item : invItem)
                );

            }
        },

        // Reducer to close the inventory
        closeInventory: (state) => {
            if (state.inventory) {
                state.inventory.closed = true; // Mark the inventory as closed
            }
            state.loading = false;
            state.error = null;
        },

        // Reducer to delete an item from the inventory
        deleteItem: (state, payload) => {
            if (!state.inventory) return;

            state.inventory.inventoryItems = state.inventory.inventoryItems.filter(
                (item) => item.id !== payload.id
            );

            state.inventory.totalItems = state.inventory.inventoryItems.length;
            state.inventory.totalStockValue = state.inventory.inventoryItems.reduce(
                (total, item) => total + item.stockValeurUnitaire * item.stockReel,
                0
            );

            state.loading = false;
            state.error = null;
        },

        createNewInventory: (state, payload) => {
            state.inventory = { ...payload };
        },

        // Reducer to set loading state
        setLoading: (state, payload) => {
            state.loading = payload;
        },

        findAll: (state) => {
            state.loading = true;
        },

        // Reducer to set error state
        setError: (state, payload) => {
            state.error = payload;
            state.loading = false;
        },
        handleAddOrUpdateItem: (state) => {
            state.loading = true;
        },
        handleDeleteItem: (state) => {
            state.loading = true;
        },
        saveInventory: (state) => {
            state.loading = true;
        },

        // Reducer to update the inventory metadata
        updateInventoryMetadata: (state, payload) => {
            if (state.inventory) {
                state.inventory.inventoryDate = payload.inventoryDate;
                state.inventory.totalStockValue = payload.totalStockValue;
                state.inventory.totalItems = payload.totalItems;
            }
        },
        fetchInventory: (state, payload) => {
            const inventory = state.inventories.find(inv => inv.id === payload);
            if (inventory) {
                state.inventory = inventory;
                state.loading = false;
                state.error = null;
            } else {
                state.error = 'Inventory not found';
                state.loading = false;
            }
        }
    },
    sagas: (A) => ({
        // Saga to fetch all inventories
        *[A.findAll]() {
            try {
                yield put(A.setLoading(true)); // Set loading state
                const { data } = yield call(inventoryService.findAll);
                yield put(A.setInventories(data.body));
                const openInventories = data.body.filter(inventory => !inventory.closed);
                const openInventory = openInventories.length > 0 ? openInventories.sort((a, b) => b.id - a.id)[0] : null;
                if (openInventory) {
                    yield put(A.setInventory(openInventory));
                    yield put(A.refreshInventoryItems());
                } else {
                    const { data } = yield call(inventoryService.saveInventory, {
                        id:0,
                        inventoryItems: [],
                        totalStockValue: 0,
                        totalItems: 0,
                        closed: false,
                        inventoryDate: moment().format('YYYY-MM-DD'),
                    }); // Call backend API
                    yield put(A.setInventory(data.body)); // Update Redux state
                }
            } catch (error) {
                yield put(A.setError(error.message)); // Set error state
            }
        },

        // Saga to fetch the inventory from the backend
        *[A.fetchInventory]({ payload }) {
            try {
                yield put(A.setLoading(true)); // Set loading state
                const { data } = yield call(inventoryService.fetchInventory, payload);
                yield put(A.setInventory(data.body));
            } catch (error) {
                yield put(A.setError(error.message)); // Set error state
            }
        },

        // Saga to handle adding or updating an item
        *[A.handleAddOrUpdateItem]({ payload }) {
            try {
                yield put(A.setLoading(true)); // Set loading state

                // Include inventory metadata in the payload
                const inventoryMetadata = {
                    inventoryDate: payload.inventoryDate,
                    totalStockValue: payload.totalStockValue,
                    totalItems: payload.totalItems,
                    id: payload.inventoryId,
                };

                const { data } = yield call(inventoryService.createOrUpdateInventory,
                    payload,
                    inventoryMetadata,
                );

                yield put(A.addOrUpdateItem(data.body));
            } catch (error) {
                yield put(A.setError(error.message)); // Set error state
            }
        },
        // Saga to handle closing the inventory
        *[A.closeInventory]({ payload }) {
            try {
                yield put(A.setLoading(true)); // Set loading state
                yield call(inventoryService.closeInventory, payload); // Call API to close the inventory
                yield put(A.closeInventory()); // Update the Redux state
            } catch (error) {
                yield put(A.setError(error.message)); // Set error state
            }
        },

        // Saga to handle deleting an item
        *[A.handleDeleteItem]({ payload }) {
            try {
                yield put(A.setLoading(true)); // Set loading state
                yield call(inventoryService.deleteInventoryItem, payload.id); // Call API to delete the item
                yield put(A.deleteItem(payload)); // Update the Redux state
            } catch (error) {
                yield put(A.setError(error.message)); // Set error state
            }
        },
        *[A.saveInventory]({ payload }) {
            try {
                yield put(A.setLoading(true)); // Set loading state
                const { data } = yield call(inventoryService.saveInventory, payload); // Call backend API
                yield put(A.setInventory(data.body)); // Update Redux state

            } catch (error) {
                yield put(A.setError(error.message)); // Set error state
            }
        },

    }),
});

export default inventorySlice;
export const inventoryActions = inventorySlice.actions;